import { sortDirectionMap } from "constants/sortDirectionMap";
import {
  OrganizationProductType,
  QueryGetProductsOrderByColumn,
  SortOrder,
} from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import ProductActionsCell from "modules/products/ProductActionsCell/ProductActionsCell";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { getRoute } from "utils/routing";
import useGetProducts from "./useGetProducts";
import useGetWorksheetProducts from "hooks/worksheetProducts/useGetWorksheetProducts";
import WorksheetProductActionsCell from "modules/products/WorksheetProductActionsCell/WorksheetProductActionsCell";

const productsInitialSortDirectionValue = SortOrder.ASC;

export default function useProductsTable({
  productTypeId,
}: {
  productTypeId: OrganizationProductType["id"];
}) {
  const { filtering, offset, sorting } = usePaginatedVariables();
  const { data, loading, error, paginatorInfo } = useGetProducts({
    page: offset.page,
    first: offset.first,
    search: filtering.debouncedSearchTerm,
    orderBy: [
      {
        column: QueryGetProductsOrderByColumn.NAME,
        order: sortDirectionMap[sorting.sortDirection] || productsInitialSortDirectionValue,
      },
    ],
    organizationProductTypeId: productTypeId,
  });
  const pagination = usePaginatedResources({
    paginatorInfo,
    paginate: offset.paginate,
  });

  const { data: worksheetProducts, refetch: refetchWorksheetProducts } = useGetWorksheetProducts({
    first: 50,
    search: filtering.debouncedSearchTerm,
    organizationProductTypeId: productTypeId,
  });

  const rows = useMemo(() => {
    const productionRows = (data ?? []).map((product: any) => ({
      ...product,
      kind: "production" as const,
    }));
    const customRows =
      offset.page === 1
        ? (worksheetProducts ?? []).map((product: any) => ({
            id: product.id,
            name: product.name,
            kind: "custom" as const,
            raw: product,
          }))
        : [];

    return [...customRows, ...productionRows];
  }, [data, worksheetProducts, offset.page]);

  const columns = useMemo(() => {
    return [
      {
        Header: "Product Name",
        accessor: "name",
        width: "80%",
        Cell: ({ value, row }) =>
          row.original.kind === "custom" ? (
            <Link
              style={{ color: "inherit" }}
              to={getRoute("product-types.worksheet-products.update", [
                ["productTypeId", productTypeId],
                ["productId", row.values.id],
              ])}
            >
              <strong>[Custom]</strong> {row.original.raw?.internalName || value}
              {row.original.raw?.internalName && row.original.raw?.name
                ? ` — ${row.original.raw.name}`
                : ""}
            </Link>
          ) : (
            <Link
              style={{ color: "inherit" }}
              to={getRoute("product-types.products.update", [
                ["productTypeId", productTypeId],
                ["productId", row.values.id],
              ])}
            >
              {row.original.subcontracted && `[Subcontracted]`}{" "}
              {row.original.unitPriceProduct && `[Unit Priced]`} {value}
            </Link>
          ),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ value, row }) =>
          row.original.kind === "custom" ? (
            <WorksheetProductActionsCell
              product={row.original.raw}
              productTypeId={productTypeId}
              onArchived={refetchWorksheetProducts}
            />
          ) : (
            <ProductActionsCell value={value} row={row} />
          ),
      },
    ];
  }, [productTypeId, refetchWorksheetProducts]);

  const tableData = useMemo(() => {
    return { columns, rows };
  }, [columns, rows]);

  return {
    data,
    loading,
    error,
    columns,
    tableData,
    pagination,
    filtering,
    paginatorInfo,
  } as const;
}
